/** Cross-tab handoff so Way Superuser stays put and the shop opens in a new tab. */
export const IMPERSONATION_HANDOFF_KEY = 'impersonationHandoff';

/** Tab-scoped impersonation shop session (sessionStorage — not shared across tabs). */
export const IMPERSONATION_USER_DATA_KEY = 'impersonationUserData';

/** Tab-scoped impersonation tunnel session (sessionStorage — not shared across tabs). */
export const IMPERSONATION_TUNNEL_DATA_KEY = 'impersonationTunnelData';

export interface ImpersonationHandoff {
  kind: 'user' | 'tunnel';
  session: Record<string, any>;
  targetRoute: string;
  createdAt: number;
}

function impersonationStore(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return sessionStorage;
}

export function getImpersonationStorageItem(key: string): string | null {
  return impersonationStore()?.getItem(key) ?? null;
}

export function setImpersonationStorageItem(key: string, value: string): void {
  impersonationStore()?.setItem(key, value);
  // Drop legacy localStorage copies so other tabs never pick them up.
  if (typeof window !== 'undefined') {
    localStorage.removeItem(key);
  }
}

export function removeImpersonationStorageItem(key: string): void {
  impersonationStore()?.removeItem(key);
  if (typeof window !== 'undefined') {
    localStorage.removeItem(key);
  }
}

/**
 * Install a pending impersonation handoff into tab-scoped sessionStorage
 * (does not overwrite normal userData / tunnelData used by other tabs).
 */
export function consumeImpersonationHandoffIntoStorage(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = localStorage.getItem(IMPERSONATION_HANDOFF_KEY);
  if (!raw) {
    return null;
  }
  try {
    const handoff = JSON.parse(raw) as ImpersonationHandoff;
    localStorage.removeItem(IMPERSONATION_HANDOFF_KEY);
    if (!handoff?.session || !handoff.targetRoute) {
      return null;
    }
    if (handoff.createdAt && Date.now() - handoff.createdAt > 2 * 60 * 1000) {
      return null;
    }
    if (handoff.kind === 'tunnel') {
      setImpersonationStorageItem(IMPERSONATION_TUNNEL_DATA_KEY, JSON.stringify(handoff.session));
      removeImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY);
    } else {
      setImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY, JSON.stringify(handoff.session));
      removeImpersonationStorageItem(IMPERSONATION_TUNNEL_DATA_KEY);
    }
    return handoff.targetRoute;
  } catch {
    localStorage.removeItem(IMPERSONATION_HANDOFF_KEY);
    return null;
  }
}

export function writeImpersonationHandoff(handoff: ImpersonationHandoff): void {
  localStorage.setItem(IMPERSONATION_HANDOFF_KEY, JSON.stringify(handoff));
}

export function clearImpersonationHandoff(): void {
  localStorage.removeItem(IMPERSONATION_HANDOFF_KEY);
}

/**
 * Clear this tab's impersonation session. Does not remove normal shop
 * `userData` / `tunnelData` (those are shared and belong to other tabs).
 */
export function clearActiveImpersonationSessions(): void {
  if (typeof window === 'undefined') {
    return;
  }
  removeImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY);
  removeImpersonationStorageItem(IMPERSONATION_TUNNEL_DATA_KEY);
  clearImpersonationHandoff();
  removeIfImpersonated('userData');
  removeIfImpersonated('tunnelData');
}

/**
 * Active shop session for THIS tab: tab-scoped impersonation first, else shared userData.
 */
export function getActiveUserDataRaw(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return (
    getImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY) || localStorage.getItem('userData')
  );
}

export function getActiveTunnelDataRaw(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return (
    getImpersonationStorageItem(IMPERSONATION_TUNNEL_DATA_KEY) || localStorage.getItem('tunnelData')
  );
}

/**
 * Parse active tunnel session for THIS tab.
 * On JSON failure, clears only the corrupt key and falls back to the other.
 */
export function readActiveTunnelData(): Record<string, any> | null {
  return (
    readJsonClearingCorrupt(IMPERSONATION_TUNNEL_DATA_KEY, true) ||
    readJsonClearingCorrupt('tunnelData', false)
  );
}

export function readActiveImpersonationUserSession(): Record<string, any> | null {
  return (
    readJsonFromImpersonation(IMPERSONATION_USER_DATA_KEY) ||
    (isImpersonatedPayload(readJson('userData')) ? readJson('userData') : null)
  );
}

export function readActiveImpersonationTunnelSession(): Record<string, any> | null {
  return (
    readJsonFromImpersonation(IMPERSONATION_TUNNEL_DATA_KEY) ||
    (isImpersonatedPayload(readJson('tunnelData')) ? readJson('tunnelData') : null)
  );
}

export function isImpersonationSessionActive(): boolean {
  return (
    isThisTabImpersonating() ||
    isImpersonatedPayload(readJson('userData')) ||
    isImpersonatedPayload(readJson('tunnelData'))
  );
}

/** True only when THIS tab has an active impersonation session (sessionStorage). */
export function isThisTabImpersonating(): boolean {
  return (
    isImpersonatedPayload(readJsonFromImpersonation(IMPERSONATION_USER_DATA_KEY)) ||
    isImpersonatedPayload(readJsonFromImpersonation(IMPERSONATION_TUNNEL_DATA_KEY))
  );
}

function isImpersonatedPayload(session: Record<string, any> | null): boolean {
  return !!session?.['impersonated'];
}

function removeIfImpersonated(key: string): void {
  const session = readJson(key);
  if (isImpersonatedPayload(session)) {
    localStorage.removeItem(key);
  }
}

function readJson(key: string): Record<string, any> | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function readJsonFromImpersonation(key: string): Record<string, any> | null {
  try {
    const raw = getImpersonationStorageItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function readJsonClearingCorrupt(
  key: string,
  impersonationScoped: boolean
): Record<string, any> | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = impersonationScoped ? getImpersonationStorageItem(key) : localStorage.getItem(key);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    if (impersonationScoped) {
      removeImpersonationStorageItem(key);
    } else {
      localStorage.removeItem(key);
    }
    return null;
  }
}
